import { useEffect, useMemo, useState } from "react";
import { ArrowUp, ArrowDown, GripVertical, CheckCircle2, XCircle } from "lucide-react";
import { fetchBlobUrl } from "../lib/api";
import { FileLinks, SampleBadge } from "./kit";

export const captionOf = (a) => a.caption || a.default_caption || a.filename.replace(/\.[a-z]+$/i, "");
const isImg = (a) => a.kind?.startsWith("image/");

export function useBlobUrl(fileId) {
  const [url, setUrl] = useState(null);
  useEffect(() => { let u; fetchBlobUrl(fileId).then((x) => { u = x; setUrl(x); }).catch(() => {}); return () => u && URL.revokeObjectURL(u); }, [fileId]);
  return url;
}

function LessonImage({ a }) {
  const url = useBlobUrl(a.file_id);
  const caption = captionOf(a);
  return (
    <figure className="my-8">
      {url ? <img src={url} alt={caption} className="mx-auto block h-auto max-h-[720px] w-auto max-w-full rounded-xl border border-black/5" data-testid={`lesson-image-${a.file_id}`} />
        : <div className="aspect-[4/3] w-full animate-pulse rounded-xl bg-muted" />}
      <figcaption className="mt-3 font-lesson text-sm italic text-[color:var(--lesson-mute)]">{caption}</figcaption>
    </figure>
  );
}

const Feedback = ({ ok, text, testid }) => (
  <p className={`mt-4 flex gap-2 rounded-lg p-4 text-[15px] ${ok ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`} data-testid={testid}>
    {ok ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <XCircle className="h-5 w-5 shrink-0" />}
    <span><b>{ok ? "That's right." : "Not quite, try again."}</b> {ok && text}</span>
  </p>
);

function ChoiceCheck({ k }) {
  const [pick, setPick] = useState(null);
  const [res, setRes] = useState(null);
  return (
    <>
      <div className="grid gap-3" role="radiogroup">
        {k.options.map((o, j) => (
          <button key={j} type="button" role="radio" aria-checked={pick === j} onClick={() => { setPick(j); setRes(null); }} data-testid={`kc-${k.id}-option-${j}`}
            className={`flex min-h-[56px] items-start gap-4 rounded-xl border px-4 py-3 text-left text-[17px] transition-colors ${pick === j ? "border-[color:var(--ink)]/30 bg-[color:var(--lesson-pick)]" : "border-black/10 bg-white hover:bg-[color:var(--lesson-pick)]/60"}`}>
            <span className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border text-xs font-semibold ${pick === j ? "border-[color:var(--ink)] bg-[color:var(--ink)] text-white" : "border-black/15 text-[color:var(--lesson-mute)]"}`}>{String.fromCharCode(65 + j)}</span>
            <span>{o}</span>
          </button>))}
      </div>
      <button type="button" className="kc-btn" disabled={pick === null} onClick={() => setRes(pick === k.correct)} data-testid={`kc-${k.id}-check`}>Check my answer</button>
      {res !== null && <Feedback ok={res} text={k.explanation} testid={`kc-${k.id}-feedback`} />}
    </>
  );
}

function OrderCheck({ k }) {
  const start = useMemo(() => {
    const idx = k.options.map((_, i) => i);
    for (let i = idx.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [idx[i], idx[j]] = [idx[j], idx[i]]; }
    if (idx.every((x, i) => x === i) && idx.length > 1) idx.reverse();
    return idx;
  }, [k.options]);
  const [order, setOrder] = useState(start);
  const [drag, setDrag] = useState(null);
  const [res, setRes] = useState(null);
  const move = (from, to) => { if (to < 0 || to >= order.length) return; const o = [...order]; const [x] = o.splice(from, 1); o.splice(to, 0, x); setOrder(o); setRes(null); };
  const arrow = "grid h-9 w-9 shrink-0 sm:h-10 sm:w-10 place-items-center rounded-lg border border-black/10 bg-white disabled:opacity-30";
  return (
    <>
      <p className="mb-4 text-sm text-[color:var(--lesson-mute)]">Drag the steps into order, or use the arrow buttons.</p>
      <ol className="grid gap-3">
        {order.map((oi, pos) => (
          <li key={oi} draggable onDragStart={() => setDrag(pos)} onDragOver={(e) => e.preventDefault()} onDrop={() => { move(drag, pos); setDrag(null); }}
            className={`flex items-center gap-2 rounded-xl border border-black/10 bg-white px-2 py-3 sm:gap-3 sm:px-3 ${drag === pos ? "opacity-50" : ""}`} data-testid={`kc-${k.id}-item-${pos}`}>
            <GripVertical className="hidden h-5 w-5 shrink-0 sm:block cursor-grab text-black/25" aria-hidden="true" />
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[color:var(--lesson-pick)] text-xs sm:h-8 sm:w-8 sm:text-sm font-semibold">{pos + 1}</span>
            <span className="min-w-0 flex-1 text-base sm:text-[17px]">{k.options[oi]}</span>
            <button type="button" className={arrow} disabled={pos === 0} onClick={() => move(pos, pos - 1)} aria-label="Move up" data-testid={`kc-${k.id}-up-${pos}`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" className={arrow} disabled={pos === order.length - 1} onClick={() => move(pos, pos + 1)} aria-label="Move down" data-testid={`kc-${k.id}-down-${pos}`}><ArrowDown className="h-4 w-4" /></button>
          </li>))}
      </ol>
      <button type="button" className="kc-btn" onClick={() => setRes(order.every((x, i) => x === i))} data-testid={`kc-${k.id}-check`}>Check the order</button>
      {res !== null && <Feedback ok={res} text={k.explanation} testid={`kc-${k.id}-feedback`} />}
    </>
  );
}

export function KnowledgeCheck({ k }) {
  return (
    <section className="my-10 rounded-2xl border border-black/10 bg-[color:var(--lesson-card)] p-4 sm:p-8" data-testid={`knowledge-check-${k.id}`}>
      <p className="lesson-eyebrow">Knowledge check</p>
      <h4 className="mb-5 mt-2 font-lesson-serif text-2xl leading-snug sm:text-[28px]">{k.prompt}</h4>
      {k.type === "order" ? <OrderCheck k={k} /> : <ChoiceCheck k={k} />}
    </section>
  );
}

function Block({ text }) {
  const lines = text.split("\n").map((x) => x.trim()).filter(Boolean);
  const out = [];
  lines.forEach((ln) => {
    const li = ln.startsWith("- ");
    const last = out[out.length - 1];
    if (li && last?.list) last.items.push(ln.slice(2));
    else if (li) out.push({ list: true, items: [ln.slice(2)] });
    else if (last && !last.list) last.text += " " + ln;
    else out.push({ list: false, text: ln });
  });
  return out.map((b, i) => b.list ? <ul key={i}>{b.items.map((t, k) => <li key={k}>{t}</li>)}</ul> : <p key={i}>{b.text}</p>);
}

function LessonBody({ text }) {
  return (text || "").split(/\n\s*\n/).map((p, i) => <Block key={i} text={p} />);
}

export function LessonArticle({ l, n }) {
  const atts = l.attachments || [];
  const [hero, ...more] = atts.filter(isImg);
  const files = atts.filter((a) => !isImg(a));
  return (
    <article className="lesson-article" data-testid={`lesson-${l.id}`}>
      <p className="lesson-eyebrow">Lesson {n}</p>
      <div className="mt-1 flex flex-wrap items-center gap-3"><h3 className="font-lesson-serif text-4xl leading-tight sm:text-5xl">{l.title}</h3><SampleBadge show={l.is_sample} /></div>
      {hero && <LessonImage a={hero} />}
      <div className="lesson-body font-lesson"><LessonBody text={l.body} /></div>
      {more.map((a) => <LessonImage key={a.file_id} a={a} />)}
      {files.length > 0 && <div className="mt-6"><FileLinks files={files.map((a) => ({ id: a.file_id, filename: a.filename }))} testid={`lesson-${l.id}-file`} /></div>}
      {(l.checks || []).filter((k) => k.prompt?.trim() && k.options?.length >= 2).map((k) => <KnowledgeCheck key={k.id} k={k} />)}
    </article>
  );
}
